import { graphql } from '@/generated';

const _MediaBaseFragment = graphql(`
  fragment MediaBase on Media {
    id
    mediaId
    uri
    kind
    time
    mimeRootType
    mimeSubType
  }
`);

const _DeviceBaseFragment = graphql(`
  fragment DeviceBase on Device {
    id
    shortId
    name
    data
    serial
    products
    config
    siteId
    lastMedia {
      lastOriginalMediaTime
      lastOriginalMediaId
      media {
        ...MediaBase
      }
    }
    site {
      id
      name
      geometry
      customer {
        name
      }
      config
    }
    userDeviceLinks {
      aspNetUser {
        id
        userName
      }
    }
    lastOriginalMedia: media(
      limit: 1
      orderBy: { time: DESC }
      where: { kind: { _eq: "Original" } }
    ) {
      ...MediaBase
    }
    lastProcessedMedia: media(
      limit: 1
      orderBy: { time: DESC }
      where: { kind: { _eq: "Processed" } }
    ) {
      ...MediaBase
    }
  }
`);

export const deviceWithoutZoneQuery = graphql(`
  query deviceWithoutZone(
    $orderBy: [DeviceOrderBy!]!
    $where: DeviceBoolExp!
    $limit: Int!
    $offset: Int!
  ) {
    device(orderBy: $orderBy, where: $where, limit: $limit, offset: $offset) {
      ...DeviceBase
    }
    deviceAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const deviceWithZoneQuery = graphql(`
  query deviceWithZone(
    $orderBy: [DeviceOrderBy!]!
    $where: DeviceBoolExp!
    $limit: Int!
    $offset: Int!
  ) {
    device(orderBy: $orderBy, where: $where, limit: $limit, offset: $offset) {
      ...DeviceBase
      apiKeys(orderBy: { createdAt: ASC }) {
        key
        reference
        description
      }
      rois {
        name
        config
        zone {
          key
        }
      }
    }
    deviceAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const deviceWithZoneQueryNoApiKeyDescription = graphql(`
  query deviceWithZoneQueryNoApiKeyDescription(
    $orderBy: [DeviceOrderBy!]!
    $where: DeviceBoolExp!
    $limit: Int!
    $offset: Int!
  ) {
    device(orderBy: $orderBy, where: $where, limit: $limit, offset: $offset) {
      ...DeviceBase
      apiKeys(orderBy: { createdAt: ASC }) {
        key
        reference
      }
      rois {
        name
        config
        zone {
          key
        }
      }
    }
    deviceAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const deviceByPkQuery = graphql(`
  query deviceByPk($id: uuid!) {
    deviceByPk(id: $id) {
      id
      shortId
      name
      data
      serial
      config
      products
      siteId
      apiKeys(orderBy: { createdAt: ASC }) {
        key
        id
        reference
        description
      }
      site {
        id
        name
        geometry
        config
      }
    }
  }
`);

export const deviceByPkQueryNoApiKeyDescription = graphql(`
  query deviceByPkQueryNoApiKeyDescription($id: uuid!) {
    deviceByPk(id: $id) {
      id
      shortId
      name
      data
      serial
      config
      products
      siteId
      apiKeys(orderBy: { createdAt: ASC }) {
        key
        id
        reference
      }
      site {
        id
        name
        geometry
        config
      }
    }
  }
`);

export const userDeviceLinksByDeviceQuery = graphql(`
  query userDeviceLinksByDevice($deviceId: uuid!) {
    userDeviceLink(where: { deviceId: { _eq: $deviceId } }) {
      id
      userId
    }
  }
`);

export const userDeviceLinksByUserAndDevicesQuery = graphql(`
  query userDeviceLinksByUserAndDevices(
    $userId: String!
    $deviceIds: [uuid!]!
  ) {
    userDeviceLink(
      where: { userId: { _eq: $userId }, deviceId: { _in: $deviceIds } }
    ) {
      deviceId
    }
  }
`);

export const roisByDeviceQuery = graphql(`
  query roiByDevice($deviceId: uuid!) {
    roi(where: { deviceId: { _eq: $deviceId } }) {
      id
      name
      config
      zoneId
    }
  }
`);

export const customerByDeviceQuery = graphql(`
  query customerByDevice($id: uuid!) {
    deviceByPk(id: $id) {
      site {
        customer {
          config
        }
      }
    }
  }
`);

export const liveImageQuery = graphql(`
  query liveImage($deviceId: uuid!) {
    originalMedia: media(
      where: { deviceId: { _eq: $deviceId }, kind: { _eq: "Original" } }
      orderBy: { time: DESC }
      limit: 2
    ) {
      ...MediaBase
    }
    processedMedia: media(
      where: { deviceId: { _eq: $deviceId }, kind: { _eq: "Processed" } }
      orderBy: { time: DESC }
      limit: 2
    ) {
      ...MediaBase
    }
    maskedMedia: media(
      where: { deviceId: { _eq: $deviceId }, kind: { _eq: "Masked" } }
      orderBy: { time: DESC }
      limit: 2
    ) {
      ...MediaBase
    }
  }
`);

export const surroundingImagesQuery = graphql(`
  query surroundingImages(
    $beforeFilter: MediaBoolExp!
    $afterFilter: MediaBoolExp!
  ) {
    beforeImages: media(
      where: $beforeFilter
      orderBy: { time: DESC }
      limit: 2
      offset: 0
    ) {
      ...MediaBase
    }
    afterImages: media(
      where: $afterFilter
      orderBy: { time: ASC }
      limit: 2
      offset: 0
    ) {
      ...MediaBase
    }
  }
`);

export const auditLogByEntityQuery = graphql(`
  query auditLogByEntity(
    $orderBy: [AuditLogOrderBy!]!
    $where: AuditLogBoolExp!
  ) {
    auditLog(orderBy: $orderBy, where: $where) {
      id
      action
      createdAt
      actorType
      entities
      data
      aspNetUser {
        userName
      }
    }
  }
`);

export const imageFilterCountsQuery = graphql(`
  query imageFilterCounts(
    $whereBefore: MediaBoolExp!
    $whereAfter: MediaBoolExp!
  ) {
    beforeTimestampCount: mediaAggregate(where: $whereBefore) {
      aggregate {
        count
      }
    }
    afterTimestampCount: mediaAggregate(where: $whereAfter) {
      aggregate {
        count
      }
    }
  }
`);

export const deviceMediaFromTimeQuery = graphql(`
  query deviceMediaFromTime(
    $deviceId: uuid!
    $fromTime: timestamptz!
    $limit: Int!
    $kind: String!
  ) {
    media(
      where: {
        deviceId: { _eq: $deviceId }
        time: { _gte: $fromTime }
        kind: { _eq: $kind }
      }
      orderBy: { time: ASC }
      limit: $limit
    ) {
      id
      mediaId
      uri
      kind
      time
      mimeRootType
      mimeSubType
    }
  }
`);

export const deviceMediaBeforeTimeQuery = graphql(`
  query deviceMediaBeforeTime(
    $deviceId: uuid!
    $beforeTime: timestamptz!
    $limit: Int!
    $kind: String!
  ) {
    media(
      where: {
        deviceId: { _eq: $deviceId }
        time: { _lt: $beforeTime }
        kind: { _eq: $kind }
      }
      orderBy: { time: DESC }
      limit: $limit
    ) {
      id
      mediaId
      uri
      kind
      time
      mimeRootType
      mimeSubType
    }
  }
`);

export const latestDeviceImageQuery = graphql(`
  query latestDeviceImage($where: MediaBoolExp) {
    media(offset: 0, limit: 1, orderBy: { time: DESC }, where: $where) {
      id
    }
  }
`);

export const userByRoleQuery = graphql(`
  query userByRole($role: String!) {
    aspNetUsers(
      where: { aspNetUserRoles: { aspNetRole: { name: { _eq: $role } } } }
    ) {
      id
      userName
      normalizedUserName
      aspNetUserRoles {
        aspNetRole {
          id
          name
          normalizedName
        }
      }
    }
  }
`);

export const snZoneQuery = graphql(`
  query snZoneQuery(
    $id: UUID!
    $distanceWithinMeters: Float!
    $longitude: Float!
    $latitude: Float!
  ) {
    snZones(
      deviceId: $id
      distanceWithinMeters: $distanceWithinMeters
      longitude: $longitude
      latitude: $latitude
    ) {
      polygon
      zoneKey
      polygonId
      zoneName
    }
  }
`);

export const zoneQuery = graphql(`
  query zoneQuery {
    zone {
      id
      key
    }
  }
`);

export const devicesQuery = graphql(`
  query devices($where: DeviceBoolExp!) {
    device(where: $where) {
      ...DeviceBase
      apiKeys {
        key
        reference
        description
      }
      rois {
        name
        config
        zone {
          key
        }
      }
    }
  }
`);

export const devicesQueryNoApiKeyDescription = graphql(`
  query devicesQueryNoApiKeyDescription($where: DeviceBoolExp!) {
    device(where: $where) {
      ...DeviceBase
      apiKeys {
        key
        reference
      }
      rois {
        name
        config
        zone {
          key
        }
      }
    }
  }
`);
